import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function Suggestions({ items, onPick, render, testid }) {
  if (!items.length) return null;
  return (
    <ul className="absolute left-0 right-0 top-full z-40 mt-1 max-h-64 overflow-y-auto rounded-xl border bg-white py-1 shadow-lg" data-testid={testid}>
      {items.map((it, i) => (
        <li key={i}>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); onPick(it); }} data-testid={`${testid}-item-${i}`}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#a2ca6b]/15 transition-colors">{render(it)}</button>
        </li>
      ))}
    </ul>
  );
}

export function ComuneInput({ value, onChange, onSelect }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const q = useDebounced(value, 200);
  useEffect(() => {
    if (!open || q.trim().length < 2) return setItems([]);
    api.get("/public/comuni", { params: { q } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [q, open]);
  return (
    <div className="relative">
      <Building2 className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
      <Input id="city" value={value} autoComplete="off" placeholder="Inizia a scrivere il comune" className="pl-9" data-testid="city-input"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && (
        <Suggestions items={items} testid="city-suggestions" onPick={(c) => { onSelect(c); setOpen(false); }}
          render={(c) => <><span className="font-medium">{c.name}</span><span className="text-xs text-slate-500">({c.province}) {c.cap}</span></>} />
      )}
    </div>
  );
}

const streetLabel = (p) => [p.street || p.name, p.housenumber].filter(Boolean).join(" ");

export function StreetInput({ value, comune, onChange, onPick }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const q = useDebounced(value);
  const ctrl = useRef(null);
  useEffect(() => {
    if (!open || q.trim().length < 3) return setItems([]);
    ctrl.current?.abort();
    ctrl.current = new AbortController();
    setBusy(true);
    api.get("/public/geo/search", { params: { q, city: comune || "" }, signal: ctrl.current.signal })
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [q, comune, open]);
  return (
    <div className="relative">
      <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
      <Input id="address" value={value} autoComplete="off" placeholder={comune ? `Via e numero civico a ${comune}` : "Via, numero civico"} className="pl-9 pr-9" data-testid="address-input"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {busy && <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-slate-400" />}
      {open && (
        <Suggestions items={items} testid="address-suggestions" onPick={(p) => { onPick(p); setOpen(false); }}
          render={(p) => <><span className="font-medium">{streetLabel(p)}</span><span className="truncate text-xs text-slate-500">{[p.postcode, p.city].filter(Boolean).join(" ")}</span></>} />
      )}
    </div>
  );
}
